import React from 'react';

import PropTypes from 'prop-types';
import Block from '../Block/Block.js';

import { Manager, Target, Popper } from 'react-popper';
import { Portal } from 'react-portal';

import ClickHandle from '../../internals/ClickHandle';

const noob = () => {};

function throttle(fn, wait) {
    var time = Date.now();
    return (...args) => {
        if (time + wait - Date.now() < 0) {
            fn(...args);
            time = Date.now();
        }
    };
}

class SelectBase extends React.PureComponent {
    static propTypes = {
        loading: PropTypes.bool,
        position: PropTypes.oneOf([
            'bottom',
            'bottom-start',
            'bottom-end',
            'top',
            'top-end',
            'top-start',
            'left',
            'left-start',
            'left-end',
            'right',
            'right-start',
            'right-end'
        ]),
        /** items list. */
        items: PropTypes.array,
        /** selected items list. */
        selectedItems: PropTypes.array,
        /** items list renderer. */
        render: PropTypes.func,
        /** popup wrapper. */
        popupWrapper: PropTypes.func,
        /** fire this function on dropdown open. */
        onOpen: PropTypes.func,
        /** fire this function on dropdown close. */
        onClose: PropTypes.func,
        /** function called when an item selected. */
        onSelect: PropTypes.func,
        /** close dropdown menu when scrolling the document. */
        closeOnScroll: PropTypes.bool,
        /** close dropdown menu when selecting an option. */
        closeOnSelect: PropTypes.bool,
        /** always force highlight first item on open . */
        highlightFirstItem: PropTypes.bool
    };

    static defaultProps = {
        position: 'bottom-start',
        items: [],
        selectedItems: [],
        render: item => {
            return { item };
        },
        popupWrapper: children => {
            return children;
        },
        onOpen: () => {},
        onClose: () => {},
        onSelect: () => {},
        loading: false,
        highlightFirstItem: false,
        closeOnScroll: true,
        closeOnSelect: true
    };

    state = {
        visible: false,
        selected: [],
        highlightedIndex: 0,
        scrollToIndex: 0
    };

    closeOnScroll = e => {
        if (e.target === this.parentElement) {
            return false;
        }

        this.handleClose();
    };

    updateState = throttle(next => {
        this.setState({
            highlightedIndex: next,
            scrollToIndex: 0
        });

        this.handleWindowScroll();
    }, 10);

    handleHighlightNext = dir => {
        const { state, parentElement } = this;
        const { items } = this.props;
        const total = items.length - 1;
        const currentIndex = state.highlightedIndex;

        if (!parentElement) {
            return;
        }

        let next = 0;
        if (currentIndex !== null) {
            if (dir === 1 && currentIndex >= total) {
                next = 0;
            } else if (dir === -1 && currentIndex <= 0) {
                next = total;
            } else {
                next = currentIndex + dir;
            }
        } else if (dir === -1) {
            next = total;
        }

        this.updateState(next);
    };

    handleWindowScroll = () => {
        const { parentElement } = this;

        if (!parentElement) return;

        var itemElement = parentElement.getElementsByClassName('active')[0];
        if (!itemElement) return;

        let top = 0;
        const currentElHeight = itemElement.offsetHeight;
        top = itemElement.offsetTop + currentElHeight;

        this.estimatedElementHeight = currentElHeight;

        const parentHeight = parentElement.offsetHeight;
        const parentScrollTop = parentElement.scrollTop;
        const viewport = parentHeight + parentScrollTop - top;

        if (viewport + currentElHeight >= parentHeight) {
            parentElement.scrollTop = top - currentElHeight;
        } else if (viewport <= 0) {
            parentElement.scrollTop = top - parentHeight;
        }
    };

    estimatedItemHeight = () => {
        if (!this.singleItemHeight) {
            const { parentElement } = this;
            if (!parentElement) return 10;
            const itemElement = parentElement.getElementsByClassName('item')[0];
            if (!itemElement) return 10;
            this.singleItemHeight = itemElement.offsetHeight;
        }

        return this.singleItemHeight;
    };

    handleHover = index => {
        this.setState({
            highlightedIndex: index,
            scrollToIndex: 0
        });
    };

    handlEenterSelect = () => {
        const { state, props, handleSelect } = this;
        const { highlightedIndex } = state;
        const { items, highlightFirstItem } = props;

        if (highlightedIndex !== null) {
            if (highlightedIndex > items.length - 1) {
                this.setState({
                    highlightedIndex: highlightFirstItem ? 0 : null
                });
            } else {
                const selectedItem = items[highlightedIndex];
                handleSelect(selectedItem, highlightedIndex);
            }
        }
    };

    onKeyDown = e => {
        let preventDefault = 1;

        const key = e.key || e.keyCode;

        if (key === 'Escape' || key === 'Esc' || key === 27) {
            this.handleClose();
        } else if (key === 'ArrowDown' || key === 40) {
            this.handleHighlightNext(1);
        } else if (key === 'ArrowUp' || key === 38) {
            this.handleHighlightNext(-1);
        } else if (key === 'Enter' || key === 13) {
            this.handlEenterSelect();
        } else {
            preventDefault = 0;
        }

        if (preventDefault) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    handleOpen = () => {
        const { props, state } = this;

        if (state.isOpen) {
            return;
        }

        document.addEventListener('keydown', this.onKeyDown, true);

        if (props.closeOnScroll) {
            document.addEventListener('scroll', this.closeOnScroll, true);
        }

        this.setState({
            isOpen: true,
            highlightedIndex: props.highlightFirstItem ? 0 : null
        });

        props.onOpen();
    };

    handleClose = () => {
        const { props, state } = this;

        if (!state.isOpen) {
            return;
        }

        document.removeEventListener('keydown', this.onKeyDown, true);
        if (props.closeOnScroll) {
            document.removeEventListener('scroll', this.closeOnScroll, true);
        }

        this.setState({
            highlightedIndex: null,
            scrollToIndex: 0,
            isOpen: false
        });

        props.onClose();
    };

    handleSelect = (item, index) => {
        const { props } = this;
        props.onSelect(item, index);
        if (props.closeOnSelect) {
            this.handleClose();
        }
    };

    handleScroll = throttle(e => {
        const scrollTo = parseInt(
            this.parentElement.scrollTop / this.estimatedItemHeight(),
            10
        );
        this.setState({
            scrollToIndex: scrollTo || 1
        });
    }, 10);

    componentWillUnmount() {
        this.handleClose();
    }

    popupManager = ({ popperProps, restProps }) => {
        const { props, state } = this;
        const { highlightedIndex, scrollToIndex } = state;
        const { items, className, footer, header, loading } = props;

        const scrollTo = scrollToIndex || highlightedIndex;

        const before = scrollTo - 250;
        const after = scrollTo + 250;

        const footerComp =
            typeof footer === 'function' ? footer(this.handleClose) : footer;
        const headerComp =
            typeof header === 'function' ? header(this.handleClose) : header;

        return props.popupWrapper(
            <Block
                className={`${className || ''}`}
                loading={loading}
                header={headerComp}
                footer={footerComp}
            >
                <div
                    className="menu"
                    onScroll={this.handleScroll}
                    ref={el => {
                        this.parentElement = el;
                    }}
                >
                    {items.map((item, index) => {
                        if (index > after || index < before) {
                            return null;
                        }

                        if (index === after) {
                            return (
                                <div
                                    key={index}
                                    style={{
                                        height:
                                            (items.length - scrollTo) *
                                            this.estimatedItemHeight()
                                    }}
                                />
                            );
                        }

                        if (index === before) {
                            return (
                                <div
                                    key={index}
                                    style={{
                                        height:
                                            (scrollTo - 250) *
                                            this.estimatedItemHeight()
                                    }}
                                />
                            );
                        }

                        const isHighlighted = highlightedIndex === index;

                        const isSelected =
                            props.selectedItems.find(i => i === item) !==
                            undefined;

                        return (
                            <div
                                key={index}
                                className={`item ${
                                    isHighlighted ? 'active' : ''
                                } ${isSelected ? 'selected' : ''}`}
                                onMouseOver={() => {
                                    this.handleHover(index);
                                }}
                                onClick={() => this.handleSelect(item, index)}
                            >
                                {props.render(
                                    item,
                                    index,
                                    isSelected,
                                    isHighlighted
                                )}
                            </div>
                        );
                    })}
                </div>
            </Block>
        );
    };

    popper = () => {
        const { props } = this;
        return (
            <Portal>
                <Popper placement={props.position}>
                    {({ popperProps, restProps }) => {
                        const { style, ...rest } = popperProps;
                        return (
                            <div
                                className="popper"
                                style={{ zIndex: 9999, ...style }}
                                {...rest}
                            >
                                <ClickHandle onClickOutside={this.handleClose}>
                                    <div>
                                        {this.popupManager(
                                            popperProps,
                                            restProps
                                        )}
                                    </div>
                                </ClickHandle>
                            </div>
                        );
                    }}
                </Popper>
            </Portal>
        );
    };

    render() {
        const { props, state } = this;
        const { isOpen } = state;
        const { children } = props;

        return (
            <Manager style={{ display: 'inline-block' }}>
                <Target preventOverflow={true}>
                    {({ targetProps }) => {
                        if (typeof children === 'function') {
                            return children({
                                ...targetProps,
                                onClick: () => {
                                    this.handleOpen();
                                }
                            });
                        }

                        const childreOnClick = children.props.onClick || noob;
                        return React.cloneElement(children, {
                            ...targetProps,
                            ...children.props,
                            onClick: (...e) => {
                                childreOnClick(...e);
                                this.handleOpen();
                            }
                        });
                    }}
                </Target>
                {isOpen && this.popper()}
            </Manager>
        );
    }
}

export default SelectBase;
